<script setup lang="ts">
import ClientSummaryCard from '@/components/clients/ClientSummaryCard.vue';
import { useClientQuickCreate } from '@/composables/clients/useClientQuickCreate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import SectionHeader from '@/components/workspace/SectionHeader.vue';
import { Plus, Workflow } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { copyToClipboard } from '~/lib/clipboard';
import { CLIENT_LIFECYCLE_STATUS_LABELS } from '~/lib/clientLifecycle';
import { classifyClientWorkspaceBucket, deriveWorkspaceClientStatus, sortClientsForWorkspace } from '~/lib/clientWorkspace';
import { formatMoney, formatShortDate } from '~/lib/formatters';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { resolveTenantOrigin } from '~/lib/tenantLinks';
import type { Client, ClientLifecycleStatus } from '~/types/models';

definePageMeta({
    layout: 'app',
    middleware: ['tenant'],
});

const clientStore = useClientStore();
const projectStore = useProjectStore();
const quotesStore = useQuotesStore();
const invoicesStore = useInvoicesStore();
const tenantStore = useTenantStore();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const requestUrl = useRequestURL();
const { tenantPath } = useTenantPath();
const { canMutate } = usePermissions();

const loading = ref(false);
const { openClientQuickCreate } = useClientQuickCreate();

useScopedPageActions(computed(() => canMutate.value ? [{
    id: 'add-client',
    label: 'Add client',
    icon: Plus,
    action: () => openClientQuickCreate(creationLifecycleStatus.value),
}] : []));

onMounted(async () => {
    loading.value = true;
    try {
        await clientStore.fetchClients();
    } finally {
        loading.value = false;
    }
});

const portalOrigin = computed(() => resolveTenantOrigin({
    tenant: tenantStore.currentTenant,
    appDomain: typeof runtimeConfig.public.siteUrl === 'string' ? runtimeConfig.public.siteUrl : undefined,
    fallbackOrigin: requestUrl.origin,
}));

const sortedClients = computed(() => sortClientsForWorkspace(clientStore.clients));

const leadRows = computed(() => sortedClients.value.filter((client) => classifyClientWorkspaceBucket(client) === 'leads'));
const opportunityRows = computed(() => sortedClients.value.filter((client) => classifyClientWorkspaceBucket(client) === 'opportunities'));
const clientRows = computed(() => sortedClients.value.filter((client) => classifyClientWorkspaceBucket(client) === 'clients'));
const pastClientRows = computed(() => sortedClients.value.filter((client) => classifyClientWorkspaceBucket(client) === 'past-clients'));

const metrics = computed(() => ({
    totalClients: sortedClients.value.length,
    leads: leadRows.value.length,
    opportunities: opportunityRows.value.length,
    clients: clientRows.value.length,
    pastClients: pastClientRows.value.length,
}));

const clientView = computed<'leads' | 'opportunities' | 'clients' | 'past-clients' | 'all'>(() => {
    const raw = String(route.query.view || '').trim().toLowerCase();
    if (raw === 'leads' || raw === 'opportunities' || raw === 'clients' || raw === 'past-clients') return raw;
    return 'all';
});

const viewConfig = computed(() => {
    const configs = {
        leads: {
            eyebrow: 'CRM',
            label: 'Enquiries',
            title: 'Enquiries and leads',
            description: 'New and early-stage records that still need qualification.',
            empty: 'No enquiries or lead-stage clients are active right now.',
            rows: leadRows.value,
        },
        opportunities: {
            eyebrow: 'CRM',
            label: 'Opportunities',
            title: 'Opportunities',
            description: 'Qualified work that still needs a commercial next step.',
            empty: 'No opportunity-stage clients are active right now.',
            rows: opportunityRows.value,
        },
        clients: {
            eyebrow: 'CRM',
            label: 'Clients',
            title: 'Clients',
            description: 'Won, onboarding, and active delivery accounts.',
            empty: 'No active client-stage records are visible right now.',
            rows: clientRows.value,
        },
        'past-clients': {
            eyebrow: 'CRM',
            label: 'Past clients',
            title: 'Past and archived',
            description: 'Paused, completed, lost, and archived records.',
            empty: 'No archived or past client records are visible right now.',
            rows: pastClientRows.value,
        },
        all: {
            eyebrow: 'CRM',
            label: 'CRM',
            title: 'Client workspace',
            description: 'Track enquiries, opportunities, active work, and past clients in one place.',
            empty: 'No client records are visible yet.',
            rows: sortedClients.value,
        },
    } as const;

    return configs[clientView.value];
});

usePageHeaderTitle(computed(() => viewConfig.value.title));

const sectionRows = computed(() => {
    if (clientView.value !== 'all') {
        return [{
            key: clientView.value,
            eyebrow: viewConfig.value.eyebrow,
            title: viewConfig.value.title,
            description: viewConfig.value.description,
            rows: viewConfig.value.rows,
            empty: viewConfig.value.empty,
        }];
    }

    return [
        {
            key: 'leads',
            eyebrow: 'Intake',
            title: 'Enquiries and leads',
            description: 'Records that still need qualification.',
            rows: leadRows.value,
            empty: 'No enquiry or lead-stage clients yet.',
        },
        {
            key: 'opportunities',
            eyebrow: 'Commercial',
            title: 'Opportunities',
            description: 'Commercial work in motion.',
            rows: opportunityRows.value,
            empty: 'No opportunity-stage client records right now.',
        },
        {
            key: 'clients',
            eyebrow: 'Delivery',
            title: 'Clients',
            description: 'Accounts in onboarding or active delivery.',
            rows: clientRows.value,
            empty: 'No active delivery-stage clients right now.',
        },
        {
            key: 'past-clients',
            eyebrow: 'Archive',
            title: 'Past and archived',
            description: 'Past work and archived records.',
            rows: pastClientRows.value,
            empty: 'No archived client records right now.',
        },
    ];
});

function formatDate(value?: unknown) {
    return formatShortDate(value, 'No update yet');
}

function clientHref(client: Client): string {
    return tenantPath(`/clients/${client.slug}`);
}

function clientTitle(client: Client): string {
    return String(client.companyName || client.name || client.contactName || 'Untitled client').trim();
}

function clientSubtitle(client: Client): string {
    const contactName = String(client.contactName || '').trim();
    const email = String(client.email || '').trim();
    const phone = String(client.contactNumber || client.phone || '').trim();

    if (contactName && email) return `${contactName} • ${email}`;
    if (email && phone) return `${email} • ${phone}`;
    if (contactName && phone) return `${contactName} • ${phone}`;
    if (contactName) return contactName;
    if (email) return email;
    if (phone) return phone;
    return 'No primary contact details yet';
}

function clientInitials(client: Client): string {
    return clientTitle(client)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'CL';
}

function clientStatus(client: Client): ClientLifecycleStatus {
    return deriveWorkspaceClientStatus(client);
}

function clientStatusLabel(client: Client): string {
    return CLIENT_LIFECYCLE_STATUS_LABELS[clientStatus(client)];
}

function clientStageLabel(client: Client): string {
    const deliveryStage = String(client.lifecycle?.deliveryStage || '').trim();
    if (deliveryStage) {
        return deliveryStage.replace(/_/g, ' ');
    }

    const pipelineStage = String(client.lifecycle?.pipelineStage || '').trim();
    if (pipelineStage) {
        return pipelineStage.replace(/_/g, ' ');
    }

    return '';
}

function clientSourceLabel(client: Client): string {
    if (client.intake?.sourceFormName) {
        return client.intake.sourceFormName;
    }

    const source = String(client.lifecycle?.source || '').trim();
    return source ? source.replace(/_/g, ' ') : 'Client record';
}

function nextMoveLabel(client: Client): string {
    switch (clientStatus(client)) {
        case 'raw_enquiry':
            return 'Review new enquiry';
        case 'lead':
        case 'prospect':
            return 'Qualify the fit';
        case 'qualified':
        case 'opportunity':
            return 'Shape the commercial next step';
        case 'proposal_sent':
            return 'Follow up on the proposal';
        case 'won':
            return 'Start onboarding';
        case 'onboarding':
            return 'Move onboarding forward';
        case 'active':
            return 'Support active delivery';
        case 'paused':
            return 'Review paused work';
        case 'completed':
        case 'past':
        case 'archived':
            return 'Keep the history tidy';
        case 'lost':
            return 'Keep context for future reactivation';
        default:
            return 'Review client record';
    }
}

function portalLink(client: Client): string | null {
    if (!client.slug) return null;
    return `${portalOrigin.value}/portal/${encodeURIComponent(client.slug)}`;
}

function openEditClient(client: Client) {
    void navigateTo(clientHref(client));
}

async function handleViewAsClientPortal(client: Client) {
    if (!client.slug || !tenantStore.currentTenantId) {
        toast.error('This client is not ready for portal preview yet.');
        return;
    }

    await navigateTo(`/portal/${encodeURIComponent(client.slug)}?asClientId=${encodeURIComponent(client.id)}&tenantId=${encodeURIComponent(tenantStore.currentTenantId)}`);
}

async function handleCopyClientPortalLink(client: Client) {
    const link = portalLink(client);
    if (!link) {
        toast.error('This client does not have a portal link yet.');
        return;
    }

    try {
        await copyToClipboard(link);
        toast.success('Client portal link copied.');
    } catch (error: any) {
        toast.error(error?.message || 'Could not copy the client portal link.');
    }
}

async function handleSendClientDigest(client: Client) {
    const recipientEmail = String(client.email || '').trim();
    const portal = portalLink(client);
    if (!recipientEmail) {
        toast.error('Add a client email address before sending a digest.');
        return;
    }
    if (!portal) {
        toast.error('Client portal link is not available yet.');
        return;
    }

    await Promise.all([
        projectStore.projects.length ? Promise.resolve() : projectStore.fetchProjects(),
        quotesStore.quotes.length ? Promise.resolve() : quotesStore.fetchAll(),
        invoicesStore.invoices.length ? Promise.resolve() : invoicesStore.fetchAll(),
    ]);

    const relatedProjects = projectStore.projects.filter((project) => (
        String(project.clientId || '').trim() === client.id
    ));
    const relatedProjectIdSet = new Set(relatedProjects.map((project) => String(project.id || '').trim()));
    const sentQuotes = quotesStore.quotes.filter((quote) => (
        quote.status === 'sent'
        && quote.clientFacing !== false
        && (
            String(quote.clientId || '').trim() === client.id
            || (quote.projectId && relatedProjectIdSet.has(String(quote.projectId).trim()))
        )
    ));
    const outstandingInvoices = invoicesStore.invoices.filter((invoice) => (
        invoice.status !== 'paid'
        && Number(invoice.amountDue || 0) > 0
        && (
            String(invoice.clientId || '').trim() === client.id
            || (invoice.projectId && relatedProjectIdSet.has(String(invoice.projectId).trim()))
        )
    ));
    const outstandingTotal = outstandingInvoices.reduce((sum, invoice) => sum + Number(invoice.amountDue || 0), 0);
    const currency = outstandingInvoices[0]?.currency || tenantStore.currentTenant?.paymentSettings?.defaultCurrency || 'GBP';
    const highlights = [
        relatedProjects.length > 0
            ? `${relatedProjects.length} project${relatedProjects.length === 1 ? '' : 's'} currently linked to this client record.`
            : 'No active projects are currently linked to this client record.',
        sentQuotes.length > 0
            ? `${sentQuotes.length} quote${sentQuotes.length === 1 ? '' : 's'} ready for review.`
            : 'No quote actions are currently waiting.',
        outstandingInvoices.length > 0
            ? `Outstanding invoice balance is ${formatMoney(outstandingTotal, currency)}.`
            : 'No outstanding invoice balance right now.',
    ];

    const toastId = toast.loading('Sending digest...');
    try {
        await $fetch('/api/send-email', {
            method: 'POST',
            headers: await getRequiredAuthHeaders(),
            body: {
                tenantId: tenantStore.currentTenantId,
                to: recipientEmail,
                template: 'weekly_digest',
                workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                clientName: client.name || clientTitle(client),
                recipientName: String(client.contactName || client.companyName || client.name || 'Client').trim(),
                actionLink: portal,
                actionLabel: 'Open portal',
                periodLabel: 'client digest',
                highlights,
            },
        });
        toast.success('Client digest sent.', { id: toastId });
    } catch (error: any) {
        toast.error(error?.message || 'Unable to send client digest.', { id: toastId });
    }
}

const creationLifecycleStatus = computed<ClientLifecycleStatus>(() => {
    switch (clientView.value) {
        case 'leads':
            return 'lead';
        case 'opportunities':
            return 'opportunity';
        case 'clients':
            return 'active';
        case 'past-clients':
            return 'past';
        default:
            return 'raw_enquiry';
    }
});
</script>

<template>
    <div class="page-shell">
        <div class="overview-metrics-grid">
            <MetricCard density="compact" label="Enquiries" :value="metrics.leads" :href="tenantPath({ path: '/clients', query: { view: 'leads' } })" :active="clientView === 'leads'" />
            <MetricCard density="compact" label="Opportunities" :value="metrics.opportunities" :href="tenantPath({ path: '/clients', query: { view: 'opportunities' } })" :active="clientView === 'opportunities'" />
            <MetricCard density="compact" label="Clients" :value="metrics.clients" :href="tenantPath({ path: '/clients', query: { view: 'clients' } })" :active="clientView === 'clients'" />
            <MetricCard density="compact" label="Past and archived" :value="metrics.pastClients" :href="tenantPath({ path: '/clients', query: { view: 'past-clients' } })" :active="clientView === 'past-clients'" />
        </div>

        <section class="space-y-6">
            <div
                v-for="section in sectionRows"
                :key="section.key"
                class="space-y-4"
            >
                <SectionHeader :title="section.title" :description="section.description" :icon="Workflow">
                    <template #actions><div class="app-section-meta hidden sm:inline-flex">{{ section.rows.length }} shown</div></template>
                </SectionHeader>

                <div v-if="loading" class="loading-state-panel">
                    Loading client records...
                </div>

                <div v-else-if="section.rows.length === 0" class="empty-state-panel">
                    {{ section.empty }}
                </div>

                <GroupedEntityList v-else>
                    <ClientSummaryCard
                        v-for="client in section.rows"
                        :key="`client-${client.id}`"
                        :client="client"
                        :href="clientHref(client)"
                        :portal-link="portalLink(client)"
                        :title="clientTitle(client)"
                        :subtitle="clientSubtitle(client)"
                        :image-url="client.headerImageURL || client.profileImageURL || ''"
                        :initials="clientInitials(client)"
                        :status-label="clientStatusLabel(client)"
                        :stage-label="clientStageLabel(client)"
                        :source-label="clientSourceLabel(client)"
                        :updated-label="formatDate(client.updatedAt)"
                        :next-move-label="nextMoveLabel(client)"
                        :can-mutate="canMutate"
                        surface="list"
                        @edit="openEditClient"
                        @view-portal="handleViewAsClientPortal"
                        @copy-portal="handleCopyClientPortalLink"
                        @send-digest="handleSendClientDigest"
                    />
                </GroupedEntityList>
            </div>
        </section>
    </div>
</template>
